import { marked } from "marked";
import dayjs from "dayjs";
import { escapeHtml } from "./tableCard.js";

/**
 * Oliver's citations, read and shown in one place.
 *
 * Since October 2026 c3 (`oliver/citations/markers.py`) writes a claim's sources after it as
 * `[12]` or `[12, S1]`: tokens of the turn's source table, each described in the message's
 * `citations` sidecar (`{token, key, kind, label, url, excerpt, count}`). A passage of a source,
 * a paragraph or a form's field, is cited by its own token, `[12.3]`, with an entry
 * `{token, source, excerpt}`. An answer numbers what it cites, a source or one of its passages, in
 * the order the tokens first appear.
 *
 * Earlier answers cite as `*([label](url); [label](url))*`, each label's markdown characters
 * backslash-escaped and a source with no page to open pointing at `source:`, with a sidecar of
 * `{n, label, url, quote}`; answers from before mid-June 2026 cite a chat file as
 * `*(attachment:<id>, p.3, §Scope)*`. Those keep rendering through the legacy path.
 */

export const NO_LINK = "source:";

// A group of source tokens in brackets that is NOT the text of a markdown link.
export const TOKEN_GROUP =
  /\[(S?\d{1,4}(?:\.\d{1,4})?(?:\s*,\s*S?\d{1,4}(?:\.\d{1,4})?)*)\](?!\()/g;

const ESCAPED = /\\([\\`*_[\]<>])/g;
const RECORD_DAY = /\b(\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01]))\b/g;
const LEGACY =
  /^\(attachment:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:,\s*p\.(\d+))?(?:,\s*§([^)]+))?\)$/i;

// What c3 links a source to: a web page or a path in this app. Anything else is not a source.
const SAFE_URL = /^(?:https?:\/\/|\/(?![/\\]))/i;

const keyOf = (label, url) => `${label}\n${url}`;

// How quotes saved before Oct 12, 2026 read: the printed form of the text blocks they came
// in. Readers only ever saw these answers' titles, so the quote stays unshown.
const SAVED_AS_BLOCKS = /^\[\{["']text["']:/;
const MARKS = /\*\*|__|^#{1,6}\s+/gm;

/** A quote or excerpt as plain words: formatting marks dropped, an old block-form quote not shown. */
function readable(quote) {
  if (!quote || SAVED_AS_BLOCKS.test(quote)) return "";
  return quote.replace(MARKS, "").replace(/\s+/g, " ").trim();
}

/** A record's or set's dates as the CRM shows them; a filename stays as it was saved. */
export function shownLabel(label) {
  if (!label.includes(" · ")) return label;
  return label.replace(RECORD_DAY, (day) => dayjs(day).format("MMM D, YYYY"));
}

/** What a source is: its record kind, or a document, or a file uploaded to the chat. */
export function kindOf({ label, url }) {
  const at = label.indexOf(" · ");
  if (at > 0) return label.slice(0, at);
  return url ? "Document" : "Chat upload";
}

/** Whether a source is a public web page (`web:<url>`), not a record or a file. */
export function isWebPage({ key, sourceKey }) {
  return String(sourceKey || key || "").startsWith("web:");
}

/** The words on a source's link: a web page is a page, everything else a record. */
export function openLabel(source) {
  return isWebPage(source) ? "Open page ↗" : "Open record ↗";
}

/** Whether a sidecar is the token format (Oct 2026 onward) rather than label-keyed. */
export function isTokenFormat(citations) {
  return Array.isArray(citations) && citations.some((c) => c && c.token);
}

/** `1 client record · 28 clinic forms · 3 sets`: records counted by kind in the order first
 * cited, then the sets as one count; the rows say what each set spans. */
export function sourcesSummary(sources) {
  const counts = new Map();
  const counted = new Set();
  let sets = 0;
  // A source with several cited passages is still one source.
  for (const { kind, key, sourceKey } of sources) {
    if (counted.has(sourceKey || key)) continue;
    counted.add(sourceKey || key);
    if (String(key).startsWith("set:")) sets += 1;
    else counts.set(kind, (counts.get(kind) || 0) + 1);
  }
  const named = [...counts].map(([kind, count]) => {
    const word = kind.toLowerCase();
    const plural =
      count > 1 && !word.endsWith("s")
        ? `${word}${word.endsWith("x") ? "es" : "s"}`
        : word;
    return `${count} ${plural}`;
  });
  if (sets) named.push(`${sets} ${sets === 1 ? "set" : "sets"}`);
  return named.join(" · ");
}

/** The tokens one text names, in reading order, each once. */
function tokensIn(text) {
  const found = [];
  for (const match of (text || "").matchAll(TOKEN_GROUP)) {
    for (const token of match[1].split(",")) {
      const t = token.trim();
      if (!found.includes(t)) found.push(t);
    }
  }
  return found;
}

// A citation group, or a reference to a file the answer shows.
const TOKEN_OR_FILE = new RegExp(
  `${TOKEN_GROUP.source}|attachment:([0-9a-zA-Z-]+)`,
  "g"
);

/**
 * The tokens an answer cites in reading order, each once: its own, and where it shows a table,
 * the citations c3 checked for that table's cells.
 */
function citedInOrder(markdown, tableTokens, idFor) {
  const found = [];
  const add = (t) => {
    if (!found.includes(t)) found.push(t);
  };
  // Fenced code shows a reference as typed; it is not the table.
  const prose = (markdown || "")
    .split(/(```[\s\S]*?```)/)
    .filter((_, i) => i % 2 === 0)
    .join("\n");
  for (const match of prose.matchAll(TOKEN_OR_FILE)) {
    if (match[1]) match[1].split(",").forEach((t) => add(t.trim()));
    else (tableTokens.get(idFor(match[2]) || match[2]) || []).forEach(add);
  }
  return found;
}

/** Each table's checked cell citations, by the table's attachment id, in sidecar order. */
function tokensByTable(citations) {
  const byTable = new Map();
  for (const entry of citations || []) {
    for (const id of entry?.tables || []) {
      if (!byTable.has(id)) byTable.set(id, []);
      byTable.get(id).push(entry.token);
    }
  }
  return byTable;
}

/** The token-format index: sources numbered by the order their tokens first appear. */
function indexSources(markdown, citations, idFor) {
  const byToken = new Map();
  for (const entry of citations || []) {
    if (entry?.token && !byToken.has(entry.token))
      byToken.set(entry.token, entry);
  }
  // A cited passage shows as its source, with the passage itself.
  const described = (token) => {
    const entry = byToken.get(token);
    const source = entry?.source && byToken.get(entry.source);
    if (!source) return entry;
    return {
      ...source,
      key: `${source.key}#${token}`,
      sourceKey: source.key,
      excerpt: entry.excerpt,
    };
  };
  const numbered = new Map();
  const sources = [];
  const number = (token) => {
    // While an answer streams the sidecar has not arrived; c3 only ever streams a token it
    // validated, so the pill shows at once and its label fills in on `complete`.
    const entry = described(token) || {
      token,
      label: "Source",
      provisional: true,
    };
    if (!numbered.has(token)) {
      const label = entry.label || entry.kind || "Source";
      const url = SAFE_URL.test(entry.url || "") ? entry.url : "";
      const source = {
        n: sources.length + 1,
        key: entry.key || token,
        sourceKey: entry.sourceKey || "",
        token,
        label,
        url,
        shown: shownLabel(label),
        kind: entry.kind || kindOf({ label, url }),
        excerpt: readable(entry.excerpt),
        count: Number(entry.count) || 0,
        provisional: Boolean(entry.provisional),
      };
      numbered.set(token, source);
      sources.push(source);
    }
    return numbered.get(token);
  };
  const tableTokens = tokensByTable(citations);
  for (const token of citedInOrder(markdown, tableTokens, idFor)) number(token);
  // Only what c3 checked for this table, so a table shown again by a later answer can never
  // borrow that answer's numbers.
  const forTable = (id, tokens) =>
    tokens
      .filter((t) => (tableTokens.get(id) || []).includes(t))
      .map((t) => numbered.get(t))
      .filter(Boolean);
  return {
    tokenFormat: true,
    forTable,
    sources,
    signature: sources
      .map((s) => `${s.key}\n${s.excerpt}\n${s.provisional ? "?" : ""}`)
      .join("\u0001"),
    byToken: (token) => numbered.get(token) || null,
    cited: (token) => {
      if (token.type !== "text" || token.tokens) return null;
      const found = tokensIn(token.text)
        .map((t) => numbered.get(t))
        .filter(Boolean);
      return found.length ? found : null;
    },
  };
}

/** The `{label, url}` pairs one italic group cites, or null when it is ordinary italics. */
function citedIn(token, files) {
  const parts = token.tokens || [];
  const framed =
    parts.length >= 3 &&
    parts.length % 2 === 1 &&
    parts.every((part, i) => {
      if (i % 2) return part.type === "link";
      const frame = i === 0 ? "(" : i === parts.length - 1 ? ")" : "; ";
      return part.type === "text" && part.raw === frame;
    });
  if (framed) {
    const links = parts.filter((part) => part.type === "link");
    if (!links.every((l) => l.href === NO_LINK || SAFE_URL.test(l.href))) {
      return null;
    }
    return links.map((link) => ({
      label: link.text.replace(ESCAPED, "$1"),
      url: link.href === NO_LINK ? "" : link.href,
    }));
  }
  const legacy = parts.length === 1 && LEGACY.exec(parts[0].raw);
  const file = legacy && files.get(legacy[1]);
  if (!file) return null;
  const anchors = [legacy[2] && `p.${legacy[2]}`, legacy[3] && `§${legacy[3]}`];
  return [
    {
      label: [
        file.original_file_name || file.id,
        ...anchors.filter(Boolean),
      ].join(", "),
      url: SAFE_URL.test(file.url || "") ? file.url : "",
    },
  ];
}

/** The legacy index: sources numbered by the italic-link groups in the markdown. */
function indexLegacy(markdown, attachments, citations) {
  const files = new Map(
    (attachments || [])
      .filter((file) => file?.id)
      .map((file) => [file.id, file])
  );
  const quotes = new Map();
  for (const { label, url, quote } of citations || []) {
    const key = keyOf(label || "", url || "");
    const words = readable(quote);
    if (words && !quotes.has(key)) quotes.set(key, words);
  }
  const byKey = new Map();
  const sources = [];
  const number = ({ label, url }) => {
    const key = keyOf(label, url);
    if (!byKey.has(key)) {
      const source = {
        n: sources.length + 1,
        key,
        label,
        url,
        shown: shownLabel(label),
        kind: kindOf({ label, url }),
        excerpt: quotes.get(key) || "",
        count: 0,
      };
      byKey.set(key, source);
      sources.push(source);
    }
  };
  marked.walkTokens(marked.lexer(markdown || ""), (token) => {
    if (token.type === "em") (citedIn(token, files) || []).forEach(number);
  });
  return {
    tokenFormat: false,
    sources,
    signature: sources.map((s) => `${s.key}\n${s.excerpt}`).join("\u0001"),
    byToken: () => null,
    forTable: () => [],
    cited: (token) => {
      const pairs = token.type === "em" && citedIn(token, files);
      return pairs
        ? pairs
            .map((pair) => byKey.get(keyOf(pair.label, pair.url)))
            .filter(Boolean)
        : null;
    },
  };
}

/**
 * Every source an answer cites, numbered, with the record's own excerpt (or, for an older
 * answer, the words it was quoted for). `signature` changes only when a source or its excerpt
 * does, so a caller can keep one index while an answer streams.
 */
export function indexCitations(
  markdown,
  { attachments = [], citations = [], idFor = () => null } = {}
) {
  TOKEN_GROUP.lastIndex = 0;
  const streamingTokens =
    !citations?.length && TOKEN_GROUP.test(markdown || "");
  TOKEN_GROUP.lastIndex = 0;
  if (isTokenFormat(citations) || streamingTokens) {
    return indexSources(markdown, citations, idFor);
  }
  return indexLegacy(markdown, attachments, citations);
}

/** One source's number in the text; MessageBubble shows its card on hover or focus. */
export function markerHtml({ n, shown, excerpt, count }) {
  const said = count ? ` ${count} records.` : excerpt ? ` “${excerpt}”` : "";
  return `<button type="button" class="citation-marker" data-citation="${n}" aria-label="${escapeHtml(
    `Source ${n}: ${shown}.${said}`
  )}">${n}</button>`;
}

/**
 * A text token's `[12, S1]` groups as pills, the rest of its text as it was. marked has already
 * escaped a `text` token's text (`inlineText`), so escaping it again showed `&#39;` for every
 * apostrophe; only what `markerHtml` builds itself is escaped. An unknown token stays as text.
 */
function tokenGroupsHtml(text, index) {
  let html = "";
  let last = 0;
  for (const match of text.matchAll(TOKEN_GROUP)) {
    const cited = match[1]
      .split(",")
      .map((t) => index.byToken(t.trim()))
      .filter(Boolean);
    html += text.slice(last, match.index);
    html += cited.length ? cited.map(markerHtml).join("") : match[0];
    last = match.index + match[0].length;
  }
  return html + text.slice(last);
}

// A chart on its plate followed only by its numbers, as the server stamps a chart's line.
const CHART_WITH_NUMBERS =
  /<p>(<span class="chart-image-wrapper">[\s\S]*?<\/span><\/span>)((?:\s*<button type="button" class="citation-marker" data-citation="\d+"[^>]*>\d+<\/button>)+)\s*<\/p>/g;

/**
 * A chart's source line as its caption, under the image inside the plate. A chart of CRM
 * figures carries no numbers and gets no caption, which is how a reader tells the two apart.
 */
export function captionCharts(html, index) {
  if (!index?.sources?.length || !html.includes("chart-image-wrapper"))
    return html;
  return html.replace(CHART_WITH_NUMBERS, (_match, figure, markers) => {
    const caption = `<span class="chart-caption">${figureSource(
      markers,
      index
    )}</span>`;
    return `<p>${figure.replace(/<\/span>$/, `${caption}</span>`)}</p>`;
  });
}

// A table card followed only by its numbers, as the server stamps a run's file line.
const CARD_WITH_NUMBERS =
  /(<div class="table-card">[\s\S]*?<\/div><\/div>)<p>((?:\s*<button type="button" class="citation-marker" data-citation="\d+"[^>]*>\d+<\/button>)+)\s*<\/p>/g;

/**
 * A table card's source line in its footer, beside the row and column count: the same line a
 * chart carries. A card of CRM figures carries no numbers and its footer stays as it is.
 */
export function captionTableCards(html, index) {
  if (!index?.sources?.length || !html.includes('class="table-card"'))
    return html;
  return html.replace(CARD_WITH_NUMBERS, (_match, card, markers) => {
    const source = `<span class="table-card__source">${figureSource(
      markers,
      index
    )}</span>`;
    return card.replace(
      /<div class="table-card__footer"><span>([^<]*)<\/span>/,
      (_m, count) =>
        `<div class="table-card__footer"><span class="table-card__meta">` +
        `<span>${count}</span>${source}</span>`
    );
  });
}

// A file card followed only by its numbers, as the answer writes them after the document.
const FILE_CARD_WITH_NUMBERS =
  /(<div class="file-card file-card--[a-z]+">[\s\S]*?<\/a>)<\/div><p>((?:\s*<button type="button" class="citation-marker" data-citation="\d+"[^>]*>\d+<\/button>)+)\s*<\/p>/g;

/** A document's source line in a footer under its card: what it was built from. */
export function captionFileCards(html, index) {
  if (!index?.sources?.length || !html.includes('class="file-card '))
    return html;
  return html.replace(
    FILE_CARD_WITH_NUMBERS,
    (_match, card, markers) =>
      `${card}<div class="file-card__footer">${figureSource(
        markers,
        index
      )}</div></div>`
  );
}

/**
 * What a chart or table was built from, as the line under a figure in a report: one source by
 * its description, several by their count. A click opens the Sources list at them
 * (`data-sources`), where each is described in full.
 */
function figureSource(markers, index) {
  const cited = [
    ...new Set(
      [...markers.matchAll(/data-citation="(\d+)"/g)].map((m) => Number(m[1]))
    ),
  ]
    .sort((a, b) => a - b)
    .map((n) => index.sources[n - 1])
    .filter(Boolean);
  if (!cited.length) return "";
  const text =
    cited.length === 1
      ? `Source: ${cited[0].shown}`
      : `${cited.length} sources`;
  const said = cited.map(({ shown }) => shown).join("; ");
  return (
    `<button type="button" class="source-line" data-sources="${cited
      .map(({ n }) => n)
      .join(",")}" aria-label="${escapeHtml(`Sources: ${said}`)}">` +
    `<i class="fas fa-book-open"></i>` +
    `<span class="source-line__text">${escapeHtml(text)}</span></button>`
  );
}

/** A `walkTokens` that shows each cited group as its numbers, where it stands. */
export function citationWalker(index) {
  return (token) => {
    if (!index) return;
    if (index.tokenFormat) {
      if (token.type !== "text" || token.tokens) return;
      TOKEN_GROUP.lastIndex = 0;
      if (!TOKEN_GROUP.test(token.text)) return;
      TOKEN_GROUP.lastIndex = 0;
      // marked's walkTokens is how a token is rewritten in place.
      token.type = "html";
      token.text = tokenGroupsHtml(token.text, index);
      return;
    }
    const cited = index.cited(token);
    if (!cited?.length) return;
    token.type = "html";
    token.text = cited.map(markerHtml).join("");
  };
}

/** What a copied source line says after its name, as its card does: the passage it cites. */
const quoted = ({ excerpt }) => (excerpt ? ` — “${excerpt}”` : "");

const sourceLine = (source) =>
  `${source.n}. ${source.shown}${quoted(source)}${
    source.url ? ` (${source.url})` : ""
  }`;

/** A card's markdown as it reads outside Oliver: each cited group as its numbers, then its sources. */
export function citationsAsText(markdown, index) {
  if (!index) return markdown;
  const used = new Set();
  let text = markdown;
  if (index.tokenFormat) {
    // Fenced code is left as typed: a list literal there is not a citation.
    text = (markdown || "")
      .split(/(```[\s\S]*?```)/)
      .map((part, i) =>
        i % 2
          ? part
          : part.replace(TOKEN_GROUP, (whole, group) => {
              const cited = group
                .split(",")
                .map((t) => index.byToken(t.trim()))
                .filter(Boolean);
              if (!cited.length) return whole;
              cited.forEach(({ n }) => used.add(n));
              return cited.map(({ n }) => `[${n}]`).join("");
            })
      )
      .join("");
  } else {
    marked.walkTokens(marked.lexer(markdown || ""), (token) => {
      const cited = index.cited(token);
      if (!cited?.length) return;
      cited.forEach(({ n }) => used.add(n));
      text = text.replace(token.raw, cited.map(({ n }) => `[${n}]`).join(""));
    });
  }
  if (!used.size) return markdown;
  const lines = index.sources.filter(({ n }) => used.has(n)).map(sourceLine);
  return `${text}\n\nSources\n${lines.join("\n")}`;
}

/** A card's markdown as plain words: every citation group gone, with the space before it. */
export function withoutCitations(markdown) {
  return (markdown || "")
    .split(/(```[\s\S]*?```)/)
    .map((part, i) =>
      i % 2
        ? part
        : part.replace(new RegExp(` ?${TOKEN_GROUP.source}`, "g"), "")
    )
    .join("");
}

/** A copied card's footnotes: the sources its numbers point at. */
export function footnotesHtml(index, numbers) {
  const items = index.sources
    .filter(({ n }) => numbers.includes(n))
    .map((source) => {
      const label = escapeHtml(source.shown);
      return `<li value="${source.n}">${
        source.url ? `<a href="${escapeHtml(source.url)}">${label}</a>` : label
      }${escapeHtml(quoted(source))}</li>`;
    });
  return items.length
    ? `<p><strong>Sources</strong></p><ol>${items.join("")}</ol>`
    : "";
}
